/**
 * Renders non-reversal line items that are not listed in the
 * `LINE_ITEMS` array in util/types.js
 *
 * The line items are rendered so that the line item code is formatted to human
 * readable form and the line total is printed as price.
 *
 * If you require another kind of presentation for your line items, add them to
 * the `LINE_ITEMS` array in util/types.js and create a specific line item
 * component for them that can be used in the `OrderBreakdown` component.
 */
import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { humanizeLineItemCode } from '../../util/data';
import { LINE_ITEMS, propTypes } from '../../util/types';

import css from './OrderBreakdown.module.css';
import { removeUnits } from '../../util/genericHelpers';
import { isLedgerCode } from '../../util/ledgerLines';

// A cart item's line is coded with its title, cut to fit Sharetribe's code
// length ("line-item/item-Walk Test Whis..."): shown with the full title.
const ITEM_CODE = /^line-item\/item-(.+)$/;
const itemTitle = (code, cartItems = []) => {
  const match = ITEM_CODE.exec(code);
  if (!match) return null;
  const start = match[1].replace(/\.\.\.$/, '');
  const item = cartItems.find(cartItem => typeof cartItem?.title === 'string' && cartItem.title.startsWith(start));
  return item ? item.title : null;
};

const LineItemUnknownItemsMaybe = props => {
  const { lineItems, cartItems, isProvider, intl } = props;

  // resolve unknown non-reversal line items
  const allItems = lineItems.filter(
    item =>
      LINE_ITEMS.indexOf(item.code) === -1 &&
      !item.reversal &&
      // The ledger's own lines (a label taken from earnings among them) have their own
      // rows (LineItemLedgerMaybe).
      !isLedgerCode(item.code)
  );

  const items = isProvider
    ? allItems.filter(item => item.includeFor.includes('provider'))
    : allItems.filter(item => item.includeFor.includes('customer'));

  return items.length > 0 ? (
    <React.Fragment>
      {items.map((item, i) => {
        const quantity = item.quantity;

        const name = itemTitle(item.code, cartItems) || humanizeLineItemCode(item.code);
        const label = quantity && quantity > 1 ? `${name} x ${quantity}` : name;

        const formattedTotal = formatMoney(intl, item.lineTotal);
        return (
          <div key={`${i}-${item.code}`} className={css.lineItem}>
            <span className={css.itemLabel}>
              {removeUnits(label) || <FormattedMessage id="OrderBreakdown.subTotal" />}
            </span>
            <span className={css.itemValue}>{formattedTotal}</span>
          </div>
        );
      })}
    </React.Fragment>
  ) : null;
};

LineItemUnknownItemsMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemUnknownItemsMaybe;
